// Every authentication and "who am I" call goes through here (CLAUDE.md: no
// component calls supabase-js directly). Errors are turned into plain sentences a
// volunteer treasurer can act on; raw Supabase messages never reach the screen.
import { supabase } from '@/lib/supabase'
import { buildProfile } from '@/auth/profile'
import type { AppUserRow, Profile, RoleRow } from '@/auth/profile'

/** An error whose message is already safe and friendly to show as-is. */
export class FriendlyError extends Error {}

type AuthLike = { message?: string; status?: number; code?: string; name?: string }

export function friendlyAuthError(err: unknown): string {
  if (err instanceof FriendlyError) return err.message
  const e = (err ?? {}) as AuthLike
  const msg = String(e.message ?? '')
  const code = String(e.code ?? '')
  if (/failed to fetch|network|load failed|timeout|fetch/i.test(msg) || e.status === 0) {
    return 'Could not reach the server. Check your internet connection and try again.'
  }
  if (code === 'invalid_credentials' || /invalid login credentials/i.test(msg)) {
    return 'That email or password is not right. Check them and try again.'
  }
  if (code === 'over_request_rate_limit' || code === 'over_email_send_rate_limit' || e.status === 429) {
    return 'Too many tries. Please wait a minute and try again.'
  }
  if (code === 'same_password' || /different from the old password/i.test(msg)) {
    return 'Your new password must be different from the temporary one.'
  }
  if (code === 'weak_password' || /password.*(weak|short|at least)/i.test(msg)) {
    return 'That password is too weak. Try a longer one with letters and numbers.'
  }
  if (code === 'email_not_confirmed') {
    return 'This account has not been activated yet. Ask an administrator to check it.'
  }
  if (code === 'user_banned' || /banned/i.test(msg)) {
    return 'This account has been switched off. Please contact the church treasurer.'
  }
  // A wrong or missing VITE_SUPABASE_ANON_KEY is also a 401: say so, not "session ended".
  if (/invalid api key|no api key/i.test(msg)) {
    return 'Invalid API key: this copy of the app is set up wrongly. Check VITE_SUPABASE_ANON_KEY in .env.'
  }
  if (code === 'session_not_found' || code === 'refresh_token_not_found' || e.status === 401) {
    return 'Your session has ended. Please sign in again.'
  }
  return 'Something went wrong. Please try again.'
}

export async function signIn(email: string, password: string): Promise<void> {
  const { error } = await supabase.auth.signInWithPassword({
    email: email.trim().toLowerCase(),
    password,
  })
  if (error) throw new FriendlyError(friendlyAuthError(error))
}

/**
 * The signed-in user's id, read from the locally stored session (no network).
 * Writes must stamp created_by with it: row-level security refuses a row whose
 * created_by is anybody else.
 */
export async function currentUserId(): Promise<string> {
  const { data } = await supabase.auth.getSession()
  const id = data.session?.user.id
  if (!id) throw new FriendlyError('Your session has ended. Please sign in again.')
  return id
}

/**
 * Ask the person for their password again, right now. Deleting records needs a password sign-in within
 * the last 10 minutes (the database checks the token), so this signs them in again as themself.
 */
export async function confirmPassword(password: string): Promise<void> {
  const { data } = await supabase.auth.getSession()
  const email = data.session?.user.email
  if (!email) throw new FriendlyError('Your session has ended. Please sign in again.')
  const { error } = await supabase.auth.signInWithPassword({ email, password })
  if (error) throw new FriendlyError(/invalid login|credentials/i.test(error.message) ? 'That password is not right.' : friendlyAuthError(error))
}

export async function signOut(): Promise<void> {
  // scope 'local': end THIS device's session only. The default ('global') would
  // also sign the person out of their phone when they log out on the laptop.
  const { error } = await supabase.auth.signOut({ scope: 'local' })
  if (error) throw new FriendlyError(friendlyAuthError(error))
}

/**
 * Report the signed-in user id (or null) now and on every change.
 *
 * The callback must NOT call back into supabase-js: doing so from inside
 * onAuthStateChange can deadlock the client. It only records the id; the caller
 * loads the profile in a separate step.
 */
export function watchSession(onUser: (userId: string | null) => void): () => void {
  let active = true
  supabase.auth.getSession().then(({ data }) => {
    if (active) onUser(data.session?.user.id ?? null)
  })
  const { data } = supabase.auth.onAuthStateChange((_event, session) => {
    if (active) onUser(session?.user.id ?? null)
  })
  return () => {
    active = false
    data.subscription.unsubscribe()
  }
}

/**
 * Load the person's church profile: their own app_user row and active roles.
 * Returns null when the login exists but has no church record (for example an
 * account added in the Supabase dashboard without being registered).
 * RLS lets everyone read exactly these two things about themself, even before
 * the forced password change.
 */
export async function loadProfile(userId: string): Promise<Profile | null> {
  const userRes = await supabase
    .from('app_user')
    .select('id, full_name, email, is_active, must_change_password')
    .eq('id', userId)
    .maybeSingle()
  if (userRes.error) throw new FriendlyError(friendlyAuthError(userRes.error))
  if (!userRes.data) return null

  const roleRes = await supabase
    .from('user_role')
    .select('id, role, department_id')
    .eq('user_id', userId)
    .is('revoked_at', null)
  if (roleRes.error) throw new FriendlyError(friendlyAuthError(roleRes.error))

  // Is this person the owner of the app? Only a convenience for showing the delete controls: the
  // database decides who may delete. Any trouble reading it just means "not the owner".
  const ownerRes = await supabase.from('app_owner').select('user_id').eq('user_id', userId).maybeSingle()
  const isOwner = !ownerRes.error && Boolean(ownerRes.data)

  return buildProfile(userRes.data as AppUserRow, (roleRes.data ?? []) as RoleRow[], isOwner)
}

/**
 * First-login password change: set the new password with Supabase Auth, then ask
 * the database to clear must_change_password. The database only agrees once the
 * password has REALLY changed, so this cannot be skipped by calling the second
 * step on its own.
 */
export async function changePasswordAtFirstLogin(newPassword: string): Promise<void> {
  const upd = await supabase.auth.updateUser({ password: newPassword })
  if (upd.error) throw new FriendlyError(friendlyAuthError(upd.error))

  const done = await supabase.rpc('complete_password_change')
  if (done.error) {
    throw new FriendlyError(
      'Your password was changed, but we could not finish setting up your account. ' +
        'Sign out, sign in again with your NEW password, and you will be able to continue.',
    )
  }
}
